import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'
import { resolveLandingAction } from '@features/landing-page-builder/model/resolveTarget'

import Button from '@shared/ui/button'

import styles from './LandingPrizeShowcaseBlock.module.scss'

/*
  prize_showcase: the prize wall of a contest landing — an optional headline
  card (image or text) plus the prize grid. The CTA opens payload.ctaActionId or
  the block's own target.
*/
interface PrizeItem {
  name?: string;
  title?: string;
  image?: string;
}

interface PrizeShowcasePayload {
  title?: string;
  headlineBanner?: string;
  headline?: string;
  headlineText?: string;
  subtitle?: string;
  subTitle?: string;
  items?: PrizeItem[];
  prizes?: PrizeItem[];
  ctaText?: string;
  ctaActionId?: string;
}

export default function LandingPrizeShowcaseBlock ({ block, payload, actions }: LandingBlockProps) {
  const {
    title,
    headlineBanner,
    headline,
    headlineText,
    subtitle,
    subTitle,
    items,
    prizes,
    ctaText,
    ctaActionId,
  } = (payload ?? {}) as PrizeShowcasePayload

  const list = (items ?? prizes ?? []).filter((item) => item?.name || item?.title || item?.image)
  const caption = subtitle || subTitle
  const headlineCopy = headline || headlineText
  const target = resolveLandingAction(actions, ctaActionId, block.target)

  if (list.length === 0 && !headlineBanner && !headlineCopy) {
    return null
  }

  return (
    <section className={styles.root}>
      {title && <h2 className={styles.root__title}>{title}</h2>}
      {caption && <p className={styles.root__subtitle}>{caption}</p>}

      {headlineBanner ? (
        <img className={styles.root__headlineImage} src={headlineBanner} alt="" loading="lazy" />
      ) : (
        headlineCopy && <div className={styles.root__headline}>{headlineCopy}</div>
      )}

      {list.length > 0 && (
        <div className={styles.root__grid}>
          {list.map((item, index) => (
            <article className={styles.prize} key={index}>
              {item.image && (
                <img className={styles.prize__image} src={item.image} alt="" loading="lazy" />
              )}
              <span className={styles.prize__name}>{item.name || item.title}</span>
            </article>
          ))}
        </div>
      )}

      {ctaText && target && (
        <div className={styles.root__cta}>
          {target.external ? (
            <Button to={target.href} size="large" asHTMLAnchor target="_blank" rel="noreferrer noopener">
              {ctaText}
            </Button>
          ) : (
            <Button to={target.href} size="large">{ctaText}</Button>
          )}
        </div>
      )}

      {ctaText && !target && block.target?.kind === 'none' && (
        // Explicitly inert CTA (e.g. a "blocked" state) — shown, not clickable.
        <div className={styles.root__cta}>
          <Button size="large" disabled>{ctaText}</Button>
        </div>
      )}
    </section>
  )
}
